<template>
  <!-- Slide-over panel -->
  <div class="detail-overlay" @click.self="$emit('close')">
    <div class="detail-panel">

      <!-- Panel header -->
      <div class="panel-header">
        <div class="panel-user">
          <div class="user-avatar">{{ userInitials }}</div>
          <div>
            <div class="user-name">{{ data?.user?.full_name || data?.user?.email || '…' }}</div>
            <div class="user-email">{{ data?.user?.email }}</div>
          </div>
          <div class="seg-badge" :class="segClass" v-if="currentScore">
            {{ currentScore.rfm_segment }}
          </div>
        </div>
        <button class="btn-close" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="panel-loading">
        <div v-for="n in 6" :key="n" class="skeleton-block"></div>
      </div>

      <div v-else-if="data" class="panel-body">

        <!-- RFM Score Cards -->
        <div class="score-grid" v-if="currentScore">
          <div class="score-card score-card--r">
            <div class="score-label">Recency</div>
            <div class="score-stars">
              <i v-for="n in 5" :key="n"
                class="fas fa-circle"
                :class="n <= currentScore.recency_score ? 'star--on' : 'star--off'"
              ></i>
            </div>
            <div class="score-detail">{{ currentScore.days_since_last_login }}d since login</div>
          </div>
          <div class="score-card score-card--f">
            <div class="score-label">Frequency</div>
            <div class="score-stars">
              <i v-for="n in 5" :key="n"
                class="fas fa-circle"
                :class="n <= currentScore.frequency_score ? 'star--on' : 'star--off'"
              ></i>
            </div>
            <div class="score-detail">{{ currentScore.sessions_last_30d }} sessions/30d</div>
          </div>
          <div class="score-card score-card--m">
            <div class="score-label">Momentum</div>
            <div class="score-stars">
              <i v-for="n in 5" :key="n"
                class="fas fa-circle"
                :class="n <= currentScore.momentum_score ? 'star--on' : 'star--off'"
              ></i>
            </div>
            <div class="score-detail">{{ currentScore.progress_delta_7d?.toFixed(1) }}% progress/7d</div>
          </div>
          <div class="score-card score-card--streak">
            <div class="score-label">Streak</div>
            <div class="score-fire">
              <i class="fas fa-fire" :class="currentScore.streak_days > 0 ? 'fire--on' : 'fire--off'"></i>
              <span>{{ currentScore.streak_days }}d</span>
            </div>
            <div class="score-detail">
              {{ currentScore.courses_enrolled }} enrolled · {{ currentScore.courses_completed }} done
            </div>
          </div>
        </div>

        <!-- At-risk banner -->
        <div v-if="currentScore?.at_risk" class="risk-banner">
          <i class="fas fa-exclamation-triangle"></i>
          <strong>At-Risk:</strong>
          <span v-for="(reason, i) in currentScore.at_risk_reasons" :key="reason">
            {{ reasonLabel(reason) }}<span v-if="i < currentScore.at_risk_reasons.length - 1">, </span>
          </span>
        </div>

        <!-- Tabs -->
        <div class="detail-tabs">
          <button
            v-for="tab in TABS"
            :key="tab.key"
            class="detail-tab"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            <i :class="tab.icon"></i> {{ tab.label }}
          </button>
        </div>

        <!-- Tab: Activity Heatmap -->
        <div v-show="activeTab === 'heatmap'" class="tab-pane">
          <p class="tab-description">When this learner is most active (last 90 days)</p>
          <EngagementHeatmap :heatmap="data.heatmap" />
        </div>

        <!-- Tab: Course Progress -->
        <div v-show="activeTab === 'courses'" class="tab-pane">
          <div v-if="data.enrollments.length === 0" class="empty-tab">
            No enrollments found.
          </div>
          <div v-for="enr in data.enrollments" :key="enr.id" class="course-card">
            <div class="course-top">
              <div class="course-name">{{ enr.course_title }}</div>
              <span class="enr-status" :class="`status--${enr.status.toLowerCase()}`">
                {{ enr.status }}
              </span>
            </div>
            <div class="course-progress-wrap">
              <div class="progress-bar-bg">
                <div
                  class="progress-bar-fill"
                  :style="{ width: enr.progress_percentage + '%' }"
                  :class="progressFillClass(enr.progress_percentage)"
                ></div>
              </div>
              <span class="progress-pct">{{ Math.round(enr.progress_percentage) }}%</span>
            </div>
            <div class="course-meta">
              <span><i class="fas fa-clock"></i> {{ formatDuration(enr.time_spent_seconds) }}</span>
              <span v-if="enr.last_accessed_at">
                <i class="fas fa-eye"></i> Last: {{ formatDate(enr.last_accessed_at) }}
              </span>
              <span v-if="enr.certificate_issued" class="cert-badge">
                <i class="fas fa-certificate"></i> Certified
              </span>
            </div>
          </div>
        </div>

        <!-- Tab: Event Timeline -->
        <div v-show="activeTab === 'timeline'" class="tab-pane">
          <div v-if="data.recent_events.length === 0" class="empty-tab">
            No events recorded yet.
          </div>
          <div class="timeline">
            <div
              v-for="(evt, i) in data.recent_events"
              :key="i"
              class="timeline-item"
            >
              <div class="timeline-dot" :class="eventDotClass(evt.event_type)">
                <i :class="eventIcon(evt.event_type)"></i>
              </div>
              <div class="timeline-body">
                <div class="timeline-event">{{ eventLabel(evt.event_type) }}</div>
                <div class="timeline-time">{{ formatDateTime(evt.timestamp) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab: Weekly Trend -->
        <div v-show="activeTab === 'trend'" class="tab-pane">
          <p class="tab-description">Sessions & watch time per week (last 8 weeks)</p>
          <div v-if="data.weekly_trend.length === 0" class="empty-tab">
            No weekly data yet.
          </div>
          <div class="trend-chart">
            <div
              v-for="week in data.weekly_trend"
              :key="week.week"
              class="trend-bar-group"
            >
              <div class="trend-bar-wrap">
                <div
                  class="trend-bar"
                  :style="{ height: trendBarHeight(week.watch_time_seconds) + 'px' }"
                  :title="`${formatDuration(week.watch_time_seconds)} watch time`"
                ></div>
              </div>
              <div class="trend-label">{{ formatWeek(week.week) }}</div>
              <div class="trend-sessions">{{ week.sessions }}s</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import api from '@/services/api'
import EngagementHeatmap from './EngagementHeatmap.vue'

const TABS = [
  { key: 'heatmap',  label: 'Activity Pattern', icon: 'fas fa-th' },
  { key: 'courses',  label: 'Courses',           icon: 'fas fa-book' },
  { key: 'timeline', label: 'Event Timeline',    icon: 'fas fa-stream' },
  { key: 'trend',    label: 'Weekly Trend',      icon: 'fas fa-chart-bar' },
]

const EVENT_CONFIG = {
  LOGIN:            { label: 'Logged in',          icon: 'fas fa-sign-in-alt', dot: 'dot--blue' },
  LOGOUT:           { label: 'Logged out',         icon: 'fas fa-sign-out-alt', dot: 'dot--gray' },
  COURSE_ENROLL:    { label: 'Enrolled in course', icon: 'fas fa-plus-circle', dot: 'dot--green' },
  COURSE_COMPLETE:  { label: 'Completed course',   icon: 'fas fa-trophy', dot: 'dot--gold' },
  COURSE_DROP:      { label: 'Dropped course',     icon: 'fas fa-minus-circle', dot: 'dot--red' },
  VIDEO_PLAY:       { label: 'Played video',       icon: 'fas fa-play', dot: 'dot--blue' },
  VIDEO_COMPLETE:   { label: 'Completed video',    icon: 'fas fa-check-circle', dot: 'dot--green' },
  VIDEO_PROGRESS:   { label: 'Video progress',     icon: 'fas fa-forward', dot: 'dot--gray' },
  QUIZ_PASS:        { label: 'Passed quiz',        icon: 'fas fa-star', dot: 'dot--gold' },
  QUIZ_FAIL:        { label: 'Failed quiz',        icon: 'fas fa-times-circle', dot: 'dot--red' },
  QUIZ_SUBMIT:      { label: 'Submitted quiz',     icon: 'fas fa-paper-plane', dot: 'dot--blue' },
}

const REASON_LABELS = {
  dormant_with_active_enrollment: 'Inactive with open enrollment',
  session_frequency_drop: 'Session frequency dropping',
  early_dropout_signal: 'Stopped after first video',
  quiz_discouragement: 'Repeated quiz failures',
}

export default {
  name: 'UserEngagementDetail',
  components: { EngagementHeatmap },

  props: {
    userId: { type: Number, required: true },
  },

  emits: ['close'],

  setup(props) {
    const loading = ref(true)
    const data = ref(null)
    const activeTab = ref('heatmap')

    const currentScore = computed(() => data.value?.current_score || null)

    const userInitials = computed(() => {
      const name = data.value?.user?.full_name || data.value?.user?.email || '?'
      return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    })

    const segClass = computed(() => {
      const seg = currentScore.value?.rfm_segment || 'casual'
      return `seg--${seg.toLowerCase()}`
    })

    const loadDetail = async () => {
      loading.value = true
      try {
        const res = await api.get(`/lms/engagement/users/${props.userId}/`)
        data.value = res.data
      } catch (e) {
        console.error('Failed to load user detail', e)
      } finally {
        loading.value = false
      }
    }

    const maxWatchTime = computed(() => {
      if (!data.value?.weekly_trend?.length) return 1
      return Math.max(...data.value.weekly_trend.map(w => w.watch_time_seconds || 0), 1)
    })

    const trendBarHeight = (seconds) =>
      Math.max(4, Math.round((seconds / maxWatchTime.value) * 80))

    const eventLabel  = (type) => EVENT_CONFIG[type]?.label || type
    const eventIcon   = (type) => EVENT_CONFIG[type]?.icon || 'fas fa-dot-circle'
    const eventDotClass = (type) => EVENT_CONFIG[type]?.dot || 'dot--gray'
    const reasonLabel = (r) => REASON_LABELS[r] || r

    const progressFillClass = (pct) => {
      if (pct >= 100) return 'fill--complete'
      if (pct >= 50)  return 'fill--mid'
      return 'fill--low'
    }

    const formatDuration = (s) => {
      if (!s) return '—'
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      return h > 0 ? `${h}h ${m}m` : `${m}m`
    }

    const formatDate = (iso) => {
      if (!iso) return '—'
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }

    const formatDateTime = (iso) => {
      if (!iso) return '—'
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
      })
    }

    const formatWeek = (isoDate) => {
      const d = new Date(isoDate)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }

    onMounted(loadDetail)
    watch(() => props.userId, loadDetail)

    return {
      TABS, loading, data, activeTab, currentScore,
      userInitials, segClass, trendBarHeight, maxWatchTime,
      eventLabel, eventIcon, eventDotClass, reasonLabel, progressFillClass,
      formatDuration, formatDate, formatDateTime, formatWeek,
    }
  }
}
</script>

<style scoped>
.detail-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4);
  z-index: 1000; display: flex; justify-content: flex-end;
  animation: fadeIn 0.2s ease;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

.detail-panel {
  width: 520px; max-width: 95vw; background: white;
  height: 100%; display: flex; flex-direction: column;
  box-shadow: -4px 0 24px rgba(0,0,0,0.15);
  animation: slideIn 0.25s ease;
  overflow: hidden;
}
@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }

/* Header */
.panel-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.25rem; border-bottom: 1px solid #f1f5f9; background: #faf5ff; flex-shrink: 0;
}
.panel-user { display: flex; align-items: center; gap: 0.75rem; }
.user-avatar {
  width: 42px; height: 42px; border-radius: 50%; background: #7c3aed;
  color: white; display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem; font-weight: 700; flex-shrink: 0;
}
.user-name { font-size: 0.95rem; font-weight: 700; color: #1e293b; }
.user-email { font-size: 0.75rem; color: #94a3b8; }

.seg-badge {
  font-size: 0.65rem; font-weight: 700; padding: 0.2rem 0.5rem;
  border-radius: 10px; text-transform: uppercase; letter-spacing: 0.05em;
}
.seg--champion  { background: #ede9fe; color: #5b21b6; }
.seg--loyal     { background: #dbeafe; color: #1d4ed8; }
.seg--promising { background: #cffafe; color: #0e7490; }
.seg--new       { background: #dcfce7; color: #166534; }
.seg--casual    { background: #f1f5f9; color: #475569; }
.seg--at_risk   { background: #ffedd5; color: #c2410c; }
.seg--dormant   { background: #fee2e2; color: #b91c1c; }

.btn-close {
  width: 32px; height: 32px; border-radius: 8px; border: 1px solid #e2e8f0;
  background: white; color: #64748b; cursor: pointer; display: flex;
  align-items: center; justify-content: center; font-size: 0.9rem;
}
.btn-close:hover { background: #fee2e2; color: #dc2626; }

/* Loading */
.panel-loading {
  padding: 1.25rem; display: flex; flex-direction: column; gap: 0.75rem;
}
.skeleton-block {
  height: 48px; background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 8px;
}
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* Body */
.panel-body { flex: 1; overflow-y: auto; padding: 1.25rem; display: flex; flex-direction: column; gap: 1rem; }

/* Score grid */
.score-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.75rem;
}
.score-card {
  padding: 0.875rem; border-radius: 10px; border: 1px solid #e2e8f0; text-align: center;
}
.score-card--r  { border-top: 3px solid #2563eb; }
.score-card--f  { border-top: 3px solid #7c3aed; }
.score-card--m  { border-top: 3px solid #16a34a; }
.score-card--streak { border-top: 3px solid #d97706; }

.score-label { font-size: 0.65rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.4rem; }
.score-stars { display: flex; justify-content: center; gap: 2px; margin-bottom: 0.35rem; font-size: 0.65rem; }
.star--on  { color: #7c3aed; }
.star--off { color: #e2e8f0; }
.score-detail { font-size: 0.65rem; color: #94a3b8; }
.score-fire { display: flex; align-items: center; justify-content: center; gap: 0.35rem; margin-bottom: 0.35rem; }
.score-fire span { font-size: 1.1rem; font-weight: 700; color: #d97706; }
.fire--on  { color: #d97706; font-size: 1.1rem; }
.fire--off { color: #e2e8f0; font-size: 1.1rem; }

/* At-risk banner */
.risk-banner {
  background: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px;
  padding: 0.75rem; font-size: 0.8rem; color: #92400e; display: flex;
  align-items: flex-start; gap: 0.5rem; flex-wrap: wrap;
}
.risk-banner i { color: #ea580c; margin-top: 0.1rem; }

/* Detail tabs */
.detail-tabs {
  display: flex; gap: 0.25rem; border-bottom: 1px solid #f1f5f9; flex-shrink: 0;
}
.detail-tab {
  padding: 0.5rem 0.75rem; font-size: 0.78rem; font-weight: 500; color: #64748b;
  background: none; border: none; border-bottom: 2px solid transparent;
  cursor: pointer; display: flex; align-items: center; gap: 0.3rem; white-space: nowrap;
  transition: all 0.15s;
}
.detail-tab:hover { color: #7c3aed; }
.detail-tab.active { color: #7c3aed; border-bottom-color: #7c3aed; }
.detail-tab i { font-size: 0.75rem; }

.tab-pane { min-height: 200px; }
.tab-description { font-size: 0.78rem; color: #94a3b8; margin: 0 0 0.75rem 0; }
.empty-tab { padding: 2rem; text-align: center; color: #94a3b8; font-size: 0.85rem; }

/* Course cards */
.course-card {
  border: 1px solid #f1f5f9; border-radius: 8px; padding: 0.875rem; margin-bottom: 0.5rem;
}
.course-top {
  display: flex; align-items: flex-start; justify-content: space-between;
  margin-bottom: 0.5rem; gap: 0.5rem;
}
.course-name { font-size: 0.85rem; font-weight: 600; color: #1e293b; }
.enr-status {
  font-size: 0.65rem; font-weight: 700; padding: 0.15rem 0.4rem;
  border-radius: 4px; text-transform: uppercase; white-space: nowrap;
}
.status--active    { background: #dbeafe; color: #1d4ed8; }
.status--completed { background: #dcfce7; color: #166534; }
.status--dropped   { background: #fee2e2; color: #b91c1c; }
.status--expired   { background: #f1f5f9; color: #64748b; }

.course-progress-wrap { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
.progress-bar-bg { flex: 1; height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
.progress-bar-fill { height: 100%; border-radius: 3px; transition: width 0.4s ease; }
.fill--complete { background: #16a34a; }
.fill--mid      { background: #7c3aed; }
.fill--low      { background: #e2e8f0; }
.progress-pct { font-size: 0.75rem; font-weight: 600; color: #374151; width: 32px; text-align: right; }

.course-meta {
  display: flex; gap: 1rem; font-size: 0.72rem; color: #94a3b8;
}
.course-meta i { margin-right: 0.25rem; }
.cert-badge { color: #d97706; font-weight: 600; }

/* Timeline */
.timeline { display: flex; flex-direction: column; gap: 0; padding-left: 0.5rem; }
.timeline-item { display: flex; gap: 0.875rem; padding-bottom: 0.875rem; position: relative; }
.timeline-item:not(:last-child)::before {
  content: ''; position: absolute; left: 10px; top: 20px; bottom: 0;
  width: 1px; background: #f1f5f9;
}
.timeline-dot {
  width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center;
  justify-content: center; font-size: 0.55rem; flex-shrink: 0; z-index: 1;
}
.dot--blue   { background: #dbeafe; color: #2563eb; }
.dot--green  { background: #dcfce7; color: #16a34a; }
.dot--gold   { background: #fef3c7; color: #d97706; }
.dot--red    { background: #fee2e2; color: #dc2626; }
.dot--gray   { background: #f1f5f9; color: #94a3b8; }

.timeline-body { padding-top: 0.1rem; }
.timeline-event { font-size: 0.82rem; font-weight: 500; color: #374151; }
.timeline-time  { font-size: 0.72rem; color: #94a3b8; margin-top: 0.15rem; }

/* Trend chart */
.trend-chart {
  display: flex; align-items: flex-end; gap: 0.5rem;
  height: 120px; padding-bottom: 2rem; position: relative;
  border-bottom: 1px solid #f1f5f9;
}
.trend-bar-group { display: flex; flex-direction: column; align-items: center; gap: 0.2rem; flex: 1; }
.trend-bar-wrap { display: flex; align-items: flex-end; height: 80px; }
.trend-bar {
  width: 24px; background: #7c3aed; border-radius: 4px 4px 0 0;
  transition: height 0.4s ease; min-height: 4px; cursor: pointer;
}
.trend-bar:hover { background: #6d28d9; }
.trend-label { font-size: 0.65rem; color: #94a3b8; text-align: center; }
.trend-sessions { font-size: 0.65rem; color: #7c3aed; font-weight: 600; }
</style>
